<template>
  <div>
    <section-page
      title="Email Integration: Gmail"
      is-short
      no-bottom-margin
      :class="[isOnboardingIntegration ? 'mt-0' : '']"
    >
      <template v-if="!isOnboardingIntegration" v-slot:right>
        <button-link
          class="mr-100"
          size=""
          :loading="isDomainLoading"
          @click="switchToCustomDomain"
        >Switch to Custom Domain</button-link>
      </template>
    </section-page>

    <section-page
      title="Email Addresses"
      description="Set up an outgoing email addresses for your users and to get an unique email to forward their incoming email addresses to."
    >
      <table-default
        class="mb-100"
        :table-data="users"
      >
        <b-table-column label="User" field="full_name" v-slot="props">
          {{ props.row.full_name }}
        </b-table-column>

        <b-table-column label="Outgoing Email" field="outgoing_email_name" v-slot="props">
            <!-- Gmail Integration Successful -->
            <template v-if="getGmailIntegrationByUserId(props.row.id) && userGmailIsIntegrated(props.row)">
              {{ getGmailIntegrationByUserId(props.row.id).data.email_address }}
              <button-link
                v-if="authUser.id === props.row.id"
                class="table_link"
                @click="unauthorizeGmail(getGmailIntegrationByUserId(props.row.id).id)"
                :loading="isDisconnecting"
              >disconnect</button-link>
            </template>

            <!-- Gmail Integration Error -->
            <template v-else-if="getGmailIntegrationByUserId(props.row.id) && userHasGmailIntegrationError(props.row)">
              <div>
                {{ getGmailIntegrationByUserId(props.row.id).data.email_address }}
                <button-link
                  v-if="authUser.id === props.row.id"
                  class="table_link"
                  @click="resetGmail(getGmailIntegrationByUserId(props.row.id).id)"
                  :loading="isDisconnecting"
                >reset</button-link>
              </div>
              <span class="mt-50 error-msg">{{ userGmailErrorMessage(props.row) }}</span>
            </template>

            <!-- Gmail Integration Not Yet Done -->
            <template v-else>
              <template v-if="authUser.id === props.row.id">
                <!-- Integrate Gmail  -->
                <button-link
                  v-if="!getGmailIntegrationByUserId(props.row.id)"
                  class="table_link ml-0"
                  @click="authorizeGmail"
                  :loading="isSaving"
                >integrate gmail</button-link>

                <!-- Integration Not Complete  -->
                <button-link
                  v-else
                  class="table_link ml-0"
                  @click="getOauthUrl(getGmailIntegrationByUserId(props.row.id).id)"
                  :loading="isSaving"
                >authorize gmail</button-link>
              </template>

              <!-- No access to do integration for other user  -->
              <template v-else>
                <em class="has-text-light">not setup</em>
              </template>
            </template>
        </b-table-column>

        <b-table-column label="Forwarding Email" field="forwarding_email_name">
          <template v-slot:header="{ column }">
            Forwarding Email <app-help-link page-uri="knowledge-base/forefront-email-set-up/forefront-email-guide/forefront-email-connecting-gsuite-google-workspace-emails/">How to Connect Your GMail</app-help-link>
          </template>

          <template v-slot="props">
            {{ props.row.forwarding_email_name }}@{{ forwardingDomain }}
            <span
              class="table_link"
              @click="copyText(props.row.forwarding_email_name + '@' + forwardingDomain)"
            ><strong>copy</strong></span>
          </template>
        </b-table-column>

        <b-table-column cell-class="action_cell" v-slot="props">
          <div class="flex flex-between" v-if="userGmailIsIntegrated(props.row)">
            <!-- Need to setup forwarder -->
            <template v-if="!getGoogleConfirmationCode(props.row.id) && !getGoogleConfirmationLink(props.row.id)">
              Set up forwarder in Gmail
            </template>
            <!-- Forward setup and waiting for verification -->
            <template v-else>
              <template v-if="userIsUnverified(props.row)">
                  <template v-if="getGoogleConfirmationCode(props.row.id)">Confirmation Code: {{getGoogleConfirmationCode(props.row.id)}}</template>
                  <template v-else-if="getGoogleConfirmationLink(props.row.id)">Confirmation Link: <a target="_blank" :href="getGoogleConfirmationLink(props.row.id)">click here</a></template>
              </template>

              <button-outlined
                  :loading="props.row.verifyButtonLoading"
                  @click="verifyEmail(props.row)">
                Verify
              </button-outlined>

              <div class="pt-2 pl-2 text">
                <span v-if="userIsUnverified(props.row)" class="has-text-danger">unverified</span>
                <span v-else-if="userIsVerifying(props.row)" class="has-text-info">verifying</span>
                <span v-else-if="userIsVerified(props.row)" class="has-text-success">verified</span>
                <span v-else class="has-text-danger">failed</span>
              </div>
            </template>
          </div>
        </b-table-column>
      </table-default>

      <p class="section_note">Note: Each user must authorize their own google account because they need to be logged in with that Google account when authorizing.</p>
    </section-page>
  </div>
</template>

<script>
import {ENUMS} from "../../../services/enums";

export default {
  name: "IntegrationGmail",
  props: {
    isOnboardingIntegration: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      forwardingDomain: process.env.MIX_MAIL_GUN_INCOMING_EMAIL_KEY,
      isSaving: false,
      isDisconnecting: false,
      isDomainLoading: false,
      defaultForm: {
        market_id: this.$store.getters.market.id,
      }
    }
  },
  computed: {
    authUser() {
      return this.$store.getters.authUser;
    },
    gmailIntegrations() {
      return this.$store.getters.gmailIntegrations;
    },
    users() {
      let users = this.$store.getters.activeUsers;
      users.verifyButtonLoading = false;
      return users;
    },
  },
  methods: {
    getGmailIntegrationByUserId(userId) {
      return this.gmailIntegrations.find(i => i.user_id === userId);
    },
    getGoogleConfirmationCode(userId) {
      return this.getGmailIntegrationByUserId(userId).data.google_forwarding_verification_code
    },
    getGoogleConfirmationLink(userId) {
      return this.getGmailIntegrationByUserId(userId).data.google_forwarding_verification_link
    },
    userHasGmailIntegrationError(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      return !!(integration && integration.token && integration.token.error);
    },
    userGmailErrorMessage(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      return (this.userHasGmailIntegrationError(user)) ? integration.token.error_description : '';
    },
    userGmailIsIntegrated(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      return !!(integration && integration.token && integration.token.access_token && integration.data && integration.data.email_address);
    },

    userIsUnverified(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        return (!integration.data.verified_at && !integration.data.offset_verifying_at);
      }

      return true;
    },
    userIsVerified(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        return integration.data.verified_at;
      }

      return false;
    },
    userIsVerifying(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        const now = new Date().getTime();
        const offsetVerifyingAt = new Date(new Date(integration.data.offset_verifying_at).toISOString()).getTime();

        return (!integration.data.verified_at && now < offsetVerifyingAt);
      }

      return false;
    },
    userFailedVerification(user) {
      let integration = this.getGmailIntegrationByUserId(user.id);
      if (integration && ((integration.data.offset_verifying_at || integration.data.verified_at))) {
        const now = new Date().getTime();
        const offsetVerifyingAt = new Date(new Date(integration.data.offset_verifying_at).toISOString()).getTime();

        return (!integration.data.verified_at && now > offsetVerifyingAt);
      }

      return false;
    },
    switchToCustomDomain() {
      const form = {
        market_id: this.$store.getters.market.id,
        email_integration_type: ENUMS.EmailIntegrationTypeEnum.MailGun.value
      }

      this.$utils.confirm(
          'Are you sure you want to switch? Gmail integrations will be deleted and will not be recoverable.',
          'Switch to Custom Domain'
      ).then(() => {
        this.isDomainLoading = true
        this.$api.markets.setEmailIntegration(form)
        .finally(() => { this.isDomainLoading = false })
      });
    },
    authorizeGmail() {
      this.isSaving = true
      this.$api.googleIntegrations.createGmailIntegration(this.defaultForm)
        .then((res) => { this.getOauthUrl(res.data.id) })
    },
    deleteIntegration(integrationId) {
      this.isDisconnecting = true
      return this.$api.integrations.deleteIntegration(integrationId)
        .finally(() => { this.isDisconnecting = false })
    },
    unauthorizeGmail(integrationId) {
      this.$utils.confirm(
        'Are you sure you want to disconnect Gmail?',
        'Disconnect Gmail Integration'
      ).then(() => {
        this.deleteIntegration(integrationId)
      })
    },
    resetGmail(integrationId) {
      this.deleteIntegration(integrationId)
        .then(() => { this.authorizeGmail() })
    },
    getOauthUrl(integrationId) {
      this.isSaving = true
      return this.$api.googleIntegrations.getOauthUrl(integrationId)
        .then((res) => { window.location.href = res.data.url })
    },
    verifyEmail(user) {
      user.verifyButtonLoading = true;
      const data = {
        user_id: user.id,
        integration_id: this.getGmailIntegrationByUserId(user.id).id,
      };

      this.$api.integrations.verifyForwardingEmail(data)
        .then(res => {
          this.$buefy.toast.open({
            message: "Verifying email address has begun. At least 5 minutes to propagate.",
            type: "is-success",
            duration: 4500
          });
        })
        .finally(() => user.verifyButtonLoading = false);
    },
    copyText(text) {
      this.$utils.copyToClipboard(text);
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.error-msg {
  display: inline-block;
  font-size: 0.75rem;
  color: $danger;
}

::v-deep .max-w-300 {
  max-width: 300px;
  word-wrap: break-word;
}

.section_domain {
  display: inline-flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 1.125rem;
  line-height: 40px;

  strong {
    margin-right: 1rem;
  }

  &--link {
    margin-left: 1rem;
    color: $link;
    font-size: 0.875rem;
    cursor: pointer;

    &:hover {
      color: $link-hover;
    }
  }
}

.section_heading {
  margin: 2rem 0 0.5rem;
  font-size: 1.25rem;
  font-weight: bold;
  line-height: 1.3;
}

.section_description {
  max-width: 600px;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.table_link {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  color: $link;
  cursor: pointer;

  &:hover {
    color: $link-hover;
  }

  strong {
    color: $link;

    &:hover {
      color: $link-hover;
    }
  }
}

.section_note {
  line-height: 1.3;
}

::v-deep .field.has-addons {
  flex-wrap: nowrap!important;
}
</style>
